import { useEffect } from 'react';
import { usePlacesStore } from './store';
import { useShortlistStore } from '@/features/shortlist';

/**
 * Однократная reconciliation при старте: удаляет из usePlacesStore.selection.comuni
 * те istats, которых нет ни в одном shortlist-списке.
 *
 * Зачем: до введения двунаправленной синхронизации (AddToShortlistButton sync)
 * «звёздочка» в SearchBar писала только в usePlacesStore, а DetailsPanel «Saved» —
 * только в useShortlistStore. В итоге накапливались «сиротские» записи в places,
 * которые не видны ни в ShortlistDrawer, ни в DetailsPanel, но рисуют BeaconPin
 * на маленьком зуме.
 *
 * Логика:
 * - istat в places И в shortlist → оставить (согласованно сохранено)
 * - istat в places, но НЕ в shortlist → удалить из places (осиротевшая запись)
 * - istat только в shortlist → добавить в places (обратная синхронизация)
 *   чтобы SearchBar-избранное отображало всё сохранённое.
 *
 * После этого cleanup AddToShortlistButton держит оба стора в синхроне.
 */
export function useReconcileSaved() {
  useEffect(() => {
    const placesState = usePlacesStore.getState();
    const shortlistState = useShortlistStore.getState();

    // Собираем union всех сохранённых istats из shortlist (все списки).
    const savedInShortlist = new Set<string>();
    for (const list of shortlistState.lists) {
      for (const istat of list.comuneIstats) savedInShortlist.add(istat);
    }

    const inPlaces = placesState.selection.comuni;

    // 1. Удалить из places то, чего нет в shortlist (сиротские записи).
    const toRemoveFromPlaces = inPlaces.filter((istat) => !savedInShortlist.has(istat));

    // 2. Добавить в places то, что есть в shortlist, но ещё не в places
    //    (сохранено через DetailsPanel до введения sync).
    const toAddToPlaces = Array.from(savedInShortlist).filter(
      (istat) => !inPlaces.includes(istat),
    );

    if (toRemoveFromPlaces.length === 0 && toAddToPlaces.length === 0) return;

    const nextComuni = [
      ...inPlaces.filter((istat) => !toRemoveFromPlaces.includes(istat)),
      ...toAddToPlaces,
    ];

    usePlacesStore.setState({
      selection: { ...placesState.selection, comuni: nextComuni },
    });
  }, []); // Намеренно пустые deps — запускаем только при монтировании.
}
